import { useCallback, useMemo, type FC } from 'react';

import { useGetOwnChats } from '@/features/api/hooks/grapevine-chat/useGetOwnChats';
import { useStartChatOnPost } from '@/features/api/hooks/grapevine-chat-events/useStartChatOnPost';
import { useGetPostById } from '@/features/api/hooks/grapevine-post/useGetPostById';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { NewChatOnEntity } from '@/features/chats/components/NewChatOnEntity';
import { findChatBetweenPersons } from '@/features/chats/utils/chat';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';

type Props = AppNativeStackScreenProps<typeof AppRoute.NewChatOnPost>;

export const ChatOnPostScreen: FC<Props> = ({ route }) => {
  const postId = route.params.postId;

  const { userId: ownUserId } = useAppState();
  const { data: post, isLoading: isPostLoading } = useGetPostById(postId);
  const { data: chats, isLoading: isChatLoading } = useGetOwnChats();
  const { mutateAsync: startChatOnPost } = useStartChatOnPost(postId);

  const sendMessage = useCallback(
    async (message: string, temporaryMediaItemIds: string[]) => {
      const response = await startChatOnPost({
        message,
        temporaryMediaItemIds
      });
      return response.chat.id;
    },
    [startChatOnPost]
  );

  const entityPerson = post?.creator;

  const existingChat = useMemo(
    () =>
      chats &&
      entityPerson &&
      findChatBetweenPersons(chats, ownUserId, entityPerson.id),
    [chats, entityPerson, ownUserId]
  );

  return (
    <NewChatOnEntity
      sendMessage={sendMessage}
      entityPerson={entityPerson}
      existingChat={existingChat}
      loading={isPostLoading || isChatLoading}
    />
  );
};
